import { useEffect, useMemo, useState } from 'react';
import { subscribeResults } from './bench.js';
import { MACHINE_PRESETS } from './catalog.js';
import { parseHash, setHash, subscribeHash } from './routing-hash.js';
import { BenchTab } from './tabs/BenchTab.js';
import { CompareTab } from './tabs/CompareTab.js';
import { DirectoryTab } from './tabs/DirectoryTab.js';
import { OverviewTab } from './tabs/OverviewTab.js';
import { ReceiptsTab } from './tabs/ReceiptsTab.js';
import { RoutingTab } from './tabs/RoutingTab.js';
import { ScanTab } from './tabs/ScanTab.js';
import type { MachineProfile } from './types.js';

export type LabTab = 'overview' | 'scan' | 'bench' | 'compare' | 'directory' | 'routing' | 'receipts';

const TABS: Array<{ id: LabTab; label: string; hint: string }> = [
  { id: 'overview', label: 'Overview', hint: 'The whole lab at a glance.' },
  { id: 'scan', label: 'Scan', hint: 'Detect your machine, see what fits.' },
  { id: 'bench', label: 'Bench', hint: 'Run real coding benchmarks.' },
  { id: 'compare', label: 'Compare', hint: 'Multi-model + cloud reference.' },
  { id: 'routing', label: 'Routing', hint: 'Local-vs-cloud per task.' },
  { id: 'directory', label: 'Directory', hint: 'Public pages by machine, GPU, model.' },
  { id: 'receipts', label: 'Receipts', hint: 'Signed proofs of every run.' },
];

interface LabShellProps {
  mode: 'beginner' | 'pro';
}

export function LabShell({ mode }: LabShellProps) {
  const initialRoute = typeof window !== 'undefined' ? parseHash(window.location.hash) : null;
  const [activeTab, setActiveTab] = useState<LabTab>(initialRoute?.tab ?? 'overview');
  const [machineId, setMachineId] = useState<string>(MACHINE_PRESETS[0]?.id ?? '');
  const [deepSlug, setDeepSlug] = useState<string | undefined>(initialRoute?.slug);
  const [resultsTick, setResultsTick] = useState(0);
  const [scannedMachine, setScannedMachine] = useState<MachineProfile | null>(null);

  useEffect(() => subscribeResults(() => setResultsTick((t) => t + 1)), []);

  useEffect(
    () =>
      subscribeHash((route) => {
        if (route) {
          setActiveTab(route.tab);
          setDeepSlug(route.slug);
        }
      }),
    [],
  );

  useEffect(() => {
    setHash({ tab: activeTab, slug: deepSlug });
  }, [activeTab, deepSlug]);

  function jumpTab(tab: LabTab, slug?: string): void {
    setActiveTab(tab);
    setDeepSlug(slug);
  }

  const machine: MachineProfile = useMemo(() => {
    if (scannedMachine) return scannedMachine;
    const found = MACHINE_PRESETS.find((m) => m.id === machineId);
    if (found) return found;
    const first = MACHINE_PRESETS[0];
    if (!first) throw new Error('No machine presets defined');
    return first;
  }, [machineId, scannedMachine]);

  return (
    <section className="panel lab-shell">
      <header className="lab-header">
        <div className="lab-brand">
          <span className="section-kicker">Local Model Lab</span>
          <h2>Can this model run on your machine? What is actually good for coding?</h2>
          <p className="lab-lede">
            See what fits, what performs, and what is worth using — built for coding agents, not generic chat.
          </p>
        </div>
        <label className="lab-machine-picker">
          <span>{scannedMachine ? 'Detected machine' : 'Working machine'}</span>
          <select
            className="lab-select"
            value={scannedMachine ? scannedMachine.id : machineId}
            onChange={(e) => {
              setMachineId(e.target.value);
              setScannedMachine(null);
            }}
          >
            {scannedMachine && <option value={scannedMachine.id}>{scannedMachine.nickname}</option>}
            {MACHINE_PRESETS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nickname}
              </option>
            ))}
          </select>
        </label>
      </header>

      <nav className="lab-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`lab-tab ${activeTab === tab.id ? 'lab-tab-active' : ''}`}
            onClick={() => jumpTab(tab.id)}
          >
            <strong>{tab.label}</strong>
            <span>{tab.hint}</span>
          </button>
        ))}
      </nav>

      <div className="lab-body" key={`${resultsTick}-${machine.id}`}>
        {activeTab === 'overview' && <OverviewTab machine={machine} mode={mode} onJumpTab={jumpTab} />}
        {activeTab === 'scan' && (
          <ScanTab
            machine={machine}
            mode={mode}
            onApplyDetected={setScannedMachine}
            scannedMachine={scannedMachine}
          />
        )}
        {activeTab === 'bench' && <BenchTab machine={machine} mode={mode} />}
        {activeTab === 'compare' && <CompareTab machine={machine} mode={mode} />}
        {activeTab === 'routing' && <RoutingTab machine={machine} mode={mode} />}
        {activeTab === 'directory' && (
          <DirectoryTab mode={mode} initialSlug={deepSlug} onChangeSlug={setDeepSlug} />
        )}
        {activeTab === 'receipts' && (
          <ReceiptsTab
            machine={machine}
            mode={mode}
            initialReceiptSlug={deepSlug}
            onChangeReceiptSlug={setDeepSlug}
          />
        )}
      </div>
    </section>
  );
}
